/**
 * Room scan → vector floor plan. The iOS app scans rooms with the LiDAR sensor (Apple RoomPlan)
 * and returns the `CapturedRoom` (one room) or `CapturedStructure` (several rooms, iOS 17) as
 * JSON — the JSON any RoomPlan app writes with `JSONEncoder`, so a scan made elsewhere imports
 * too. RoomPlan works in metres, y up, with the floor in the x–z plane; the plan is drawn from
 * above in millimetres, y up as in CAD (plan y = −z). Walls are drawn as lines (RoomPlan gives no
 * thickness), doors with their swing, windows as a frame across the wall, objects as their
 * footprint, and the rooms RoomPlan recognised (iOS 17 sections) as names.
 * See docs/decisions/0016-remediation-roomscan-quote-acceptance.md.
 */
import { boundsOf } from './dxf.ts';
import type { PlanLayer, VectorPlan } from './vector.ts';

type Vec3 = [number, number, number];

/** A wall, door, window, opening or floor (RoomPlan `CapturedRoom.Surface`). */
interface ScanSurface {
  category: Record<string, unknown>;
  /** Width, height, depth (m). */
  dimensions: Vec3;
  /** 4×4, column-major (simd_float4x4 as JSON). */
  transform: number[];
  /** Curved walls (iOS 17): radius and angles (radians, as RoomPlan's Measurement values). */
  curve?: { radius: number; startAngle: number; endAngle: number } | null;
  polygonCorners?: Vec3[];
  story?: number;
}
interface ScanObject {
  category: Record<string, unknown>;
  dimensions: Vec3;
  transform: number[];
  story?: number;
}
interface ScanSection {
  label: Record<string, unknown> | string;
  center: Vec3;
  story?: number;
}
interface CapturedRoom {
  walls: ScanSurface[];
  doors?: ScanSurface[];
  windows?: ScanSurface[];
  openings?: ScanSurface[];
  floors?: ScanSurface[];
  objects?: ScanObject[];
  sections?: ScanSection[];
  story?: number;
}

export interface RoomScan {
  rooms: CapturedRoom[];
  /** Floors in the scan (RoomPlan `story`), lowest first. */
  stories: number[];
  counts: { walls: number; doors: number; windows: number; openings: number; objects: number; rooms: number };
}

export class RoomScanError extends Error {}

const isVec3 = (v: unknown): v is Vec3 =>
  Array.isArray(v) && v.length >= 3 && v.slice(0, 3).every(Number.isFinite);
const isMatrix = (v: unknown): v is number[] =>
  Array.isArray(v) && v.length === 16 && v.every(Number.isFinite);

/**
 * simd types encode as arrays; a 4×4 matrix as 16 numbers or as 4 columns of 4. Returns null
 * for anything else.
 */
function matrix(v: unknown): number[] | null {
  if (isMatrix(v)) return v;
  if (Array.isArray(v) && v.length === 4 && v.every((c) => Array.isArray(c) && c.length === 4))
    return matrix((v as number[][]).flat());
  return null;
}

const items = <T>(v: unknown, ok: (x: Record<string, unknown>) => T | null): T[] =>
  Array.isArray(v)
    ? v
        .map((x) => (x && typeof x === 'object' ? ok(x as Record<string, unknown>) : null))
        .filter((x): x is T => !!x)
    : [];

function surface(x: Record<string, unknown>): ScanSurface | null {
  const transform = matrix(x.transform);
  if (!transform || !isVec3(x.dimensions)) return null;
  const c = x.curve as ScanSurface['curve'];
  const num = (v: unknown) =>
    typeof v === 'number' ? v : typeof v === 'object' && v ? Number((v as { value?: number }).value) : NaN;
  return {
    category: (x.category ?? {}) as Record<string, unknown>,
    dimensions: x.dimensions as Vec3,
    transform,
    curve:
      c && Number.isFinite(num(c.radius))
        ? { radius: num(c.radius), startAngle: num(c.startAngle), endAngle: num(c.endAngle) }
        : null,
    ...(Array.isArray(x.polygonCorners)
      ? { polygonCorners: (x.polygonCorners as unknown[]).filter(isVec3) }
      : {}),
    ...(typeof x.story === 'number' ? { story: x.story } : {}),
  };
}
const object = (x: Record<string, unknown>): ScanObject | null => {
  const transform = matrix(x.transform);
  if (!transform || !isVec3(x.dimensions)) return null;
  return {
    category: (x.category ?? {}) as Record<string, unknown>,
    dimensions: x.dimensions as Vec3,
    transform,
    ...(typeof x.story === 'number' ? { story: x.story } : {}),
  };
};
const section = (x: Record<string, unknown>): ScanSection | null =>
  isVec3(x.center) && x.label
    ? {
        label: x.label as ScanSection['label'],
        center: x.center,
        ...(typeof x.story === 'number' ? { story: x.story } : {}),
      }
    : null;

function room(x: Record<string, unknown>): CapturedRoom {
  return {
    walls: items(x.walls, surface),
    doors: items(x.doors, surface),
    windows: items(x.windows, surface),
    openings: items(x.openings, surface),
    floors: items(x.floors, surface),
    objects: items(x.objects, object),
    sections: items(x.sections, section),
    ...(typeof x.story === 'number' ? { story: x.story } : {}),
  };
}

/** A `CapturedRoom` or a `CapturedStructure` (its `rooms`), as JSON. */
export function parseRoomScan(bytes: Uint8Array | string): RoomScan {
  let data: unknown;
  try {
    data = JSON.parse(typeof bytes === 'string' ? bytes : new TextDecoder().decode(bytes));
  } catch {
    throw new RoomScanError('not JSON');
  }
  const d = data as Record<string, unknown>;
  const rooms = Array.isArray(d?.rooms)
    ? (d.rooms as unknown[])
        .filter((r) => r && typeof r === 'object')
        .map((r) => room(r as Record<string, unknown>))
    : d && Array.isArray(d.walls)
      ? [room(d)]
      : [];
  if (!rooms.some((r) => r.walls.length)) throw new RoomScanError('no walls');
  const storyOf = (r: CapturedRoom, s?: { story?: number }) => s?.story ?? r.story ?? 0;
  const stories = new Set<number>();
  for (const r of rooms) for (const w of r.walls) stories.add(storyOf(r, w));
  const sum = (f: (r: CapturedRoom) => number) => rooms.reduce((s, r) => s + f(r), 0);
  return {
    rooms,
    stories: [...stories].sort((a, b) => a - b),
    counts: {
      walls: sum((r) => r.walls.length),
      doors: sum((r) => r.doors?.length ?? 0),
      windows: sum((r) => r.windows?.length ?? 0),
      openings: sum((r) => r.openings?.length ?? 0),
      objects: sum((r) => r.objects?.length ?? 0),
      rooms: sum((r) => r.sections?.length ?? 0),
    },
  };
}

// ---------- geometry ----------

/** World point (m) → plan point (mm, y up seen from above). */
const plan = (x: number, z: number): [number, number] => [x * 1000, -z * 1000];

/** A point of the element's own frame (local x, local z) in the world, as a plan point. */
function at(m: number[], lx: number, lz: number): [number, number] {
  const x = m[12]! + m[0]! * lx + m[8]! * lz;
  const z = m[14]! + m[2]! * lx + m[10]! * lz;
  return plan(x, z);
}

const flat = (pts: Array<[number, number]>) => pts.flat();

/** The wall's centre line: a segment along its local x, or an arc for a curved wall. */
function wallLine(w: ScanSurface): number[] {
  const m = w.transform;
  const half = w.dimensions[0] / 2;
  if (
    w.curve &&
    w.curve.radius > 0 &&
    Number.isFinite(w.curve.startAngle) &&
    Number.isFinite(w.curve.endAngle)
  ) {
    const { radius: r, startAngle: a0, endAngle: a1 } = w.curve;
    const n = Math.max(4, Math.ceil(Math.abs(a1 - a0) / (Math.PI / 24)));
    const pts: Array<[number, number]> = [];
    for (let i = 0; i <= n; i++) {
      const a = a0 + ((a1 - a0) * i) / n;
      pts.push(at(m, r * Math.cos(a), r * Math.sin(a)));
    }
    return flat(pts);
  }
  return flat([at(m, -half, 0), at(m, half, 0)]);
}

/** A door: the leaf open at 90° and its swing (quarter circle), hinge on the left. */
function doorLines(d: ScanSurface): number[][] {
  const m = d.transform;
  const w = d.dimensions[0];
  const half = w / 2;
  const arc: Array<[number, number]> = [];
  for (let i = 0; i <= 12; i++) {
    const a = ((Math.PI / 2) * i) / 12;
    arc.push(at(m, -half + w * Math.cos(a), w * Math.sin(a)));
  }
  return [flat([at(m, -half, 0), at(m, -half, w)]), flat(arc)];
}

/** A window or an opening across the wall: a frame 100 mm deep (or the scanned depth). */
function frame(s: ScanSurface, depthM = 0.1): number[][] {
  const m = s.transform;
  const half = s.dimensions[0] / 2;
  const d = Math.max(s.dimensions[2] || 0, depthM) / 2;
  return [
    flat([at(m, -half, -d), at(m, half, -d), at(m, half, d), at(m, -half, d), at(m, -half, -d)]),
    flat([at(m, -half, 0), at(m, half, 0)]),
  ];
}

const footprint = (o: ScanObject): number[] => {
  const m = o.transform;
  const [w, , d] = o.dimensions;
  return flat([
    at(m, -w / 2, -d / 2),
    at(m, w / 2, -d / 2),
    at(m, w / 2, d / 2),
    at(m, -w / 2, d / 2),
    at(m, -w / 2, -d / 2),
  ]);
};

/** The RoomPlan category of an enum encoded by Swift (`{"door":{"isOpen":false}}`, `"bedroom"`). */
export const categoryOf = (c: Record<string, unknown> | string): string =>
  typeof c === 'string' ? c : (Object.keys(c)[0] ?? '');

type Names = Record<string, string>;

/** Room and object names, per language. */
export const SCAN_NAMES: Record<'fr' | 'en' | 'nl' | 'de', Names> = {
  fr: {
    bedroom: 'Chambre',
    bathroom: 'Salle de bain',
    kitchen: 'Cuisine',
    livingRoom: 'Séjour',
    diningRoom: 'Salle à manger',
    storage: 'Rangement',
    refrigerator: 'Frigo',
    stove: 'Cuisinière',
    bed: 'Lit',
    sink: 'Évier',
    washerDryer: 'Lave-linge',
    toilet: 'WC',
    bathtub: 'Baignoire',
    oven: 'Four',
    dishwasher: 'Lave-vaisselle',
    table: 'Table',
    sofa: 'Canapé',
    chair: 'Chaise',
    fireplace: 'Cheminée',
    television: 'TV',
    stairs: 'Escalier',
  },
  en: {
    bedroom: 'Bedroom',
    bathroom: 'Bathroom',
    kitchen: 'Kitchen',
    livingRoom: 'Living room',
    diningRoom: 'Dining room',
    storage: 'Storage',
    refrigerator: 'Fridge',
    stove: 'Cooker',
    bed: 'Bed',
    sink: 'Sink',
    washerDryer: 'Washing machine',
    toilet: 'WC',
    bathtub: 'Bath',
    oven: 'Oven',
    dishwasher: 'Dishwasher',
    table: 'Table',
    sofa: 'Sofa',
    chair: 'Chair',
    fireplace: 'Fireplace',
    television: 'TV',
    stairs: 'Stairs',
  },
  nl: {
    bedroom: 'Slaapkamer',
    bathroom: 'Badkamer',
    kitchen: 'Keuken',
    livingRoom: 'Woonkamer',
    diningRoom: 'Eetkamer',
    storage: 'Berging',
    refrigerator: 'Koelkast',
    stove: 'Fornuis',
    bed: 'Bed',
    sink: 'Spoelbak',
    washerDryer: 'Wasmachine',
    toilet: 'Wc',
    bathtub: 'Bad',
    oven: 'Oven',
    dishwasher: 'Vaatwasser',
    table: 'Tafel',
    sofa: 'Zetel',
    chair: 'Stoel',
    fireplace: 'Haard',
    television: 'Tv',
    stairs: 'Trap',
  },
  de: {
    bedroom: 'Schlafzimmer',
    bathroom: 'Badezimmer',
    kitchen: 'Küche',
    livingRoom: 'Wohnzimmer',
    diningRoom: 'Esszimmer',
    storage: 'Abstellraum',
    refrigerator: 'Kühlschrank',
    stove: 'Herd',
    bed: 'Bett',
    sink: 'Spüle',
    washerDryer: 'Waschmaschine',
    toilet: 'WC',
    bathtub: 'Badewanne',
    oven: 'Backofen',
    dishwasher: 'Geschirrspüler',
    table: 'Tisch',
    sofa: 'Sofa',
    chair: 'Stuhl',
    fireplace: 'Kamin',
    television: 'TV',
    stairs: 'Treppe',
  },
};

export interface RoomScanOptions {
  /** Floor to draw (RoomPlan story); the lowest when omitted. */
  story?: number;
  /** Language of room and object names. */
  locale?: 'fr' | 'en' | 'nl' | 'de';
  /** Draw the objects RoomPlan recognised (furniture, appliances). */
  objects?: boolean;
}

/** The layer names of a room scan plan, in drawing order. */
export const SCAN_LAYERS = ['walls', 'doors', 'windows', 'openings', 'objects', 'rooms'] as const;

/** Room scan → vector floor plan (mm), one floor. */
export function roomScanToPlan(scan: RoomScan, opts: RoomScanOptions = {}): VectorPlan {
  const story = opts.story ?? scan.stories[0] ?? 0;
  const names = SCAN_NAMES[opts.locale ?? 'fr'];
  const layer = (name: string): PlanLayer => ({ name, lines: [], texts: [] });
  const L = Object.fromEntries(SCAN_LAYERS.map((n) => [n, layer(n)])) as Record<
    (typeof SCAN_LAYERS)[number],
    PlanLayer
  >;
  for (const r of scan.rooms) {
    const on = (s: { story?: number }) => (s.story ?? r.story ?? 0) === story;
    for (const w of r.walls.filter(on)) L.walls.lines.push(wallLine(w));
    for (const d of (r.doors ?? []).filter(on)) L.doors.lines.push(...doorLines(d));
    for (const w of (r.windows ?? []).filter(on)) L.windows.lines.push(...frame(w));
    for (const o of (r.openings ?? []).filter(on)) L.openings.lines.push(frame(o)[0]!);
    if (opts.objects !== false)
      for (const o of (r.objects ?? []).filter(on)) {
        L.objects.lines.push(footprint(o));
        const name = names[categoryOf(o.category)];
        const [x, y] = at(o.transform, 0, 0);
        if (name) L.objects.texts.push({ x, y, h: 120, rot: 0, text: name, align: 'middle' });
      }
    for (const s of (r.sections ?? []).filter(on)) {
      const name = names[categoryOf(s.label)];
      if (!name) continue;
      const [x, y] = plan(s.center[0], s.center[2]);
      L.rooms.texts.push({ x, y, h: 250, rot: 0, text: name, align: 'middle' });
    }
  }
  const out: VectorPlan = {
    v: 1,
    bounds: [0, 0, 1, 1],
    layers: SCAN_LAYERS.map((n) => L[n]).filter((l) => l.lines.length || l.texts.length),
  };
  out.bounds = boundsOf(out);
  return out;
}
